import { isNullOrWhitespace, setFirstLetterToLowerCase } from "@common/stringOperations";
import { InputFieldTypeEnum } from "../../inputFields/InputFields.types.ts";
import { DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { useMemo } from "react";
import { useTranslation } from "@localization/useTranslation";
import { useCustomField } from "./customField.hooks.ts";
import { useCountriesQuery } from "@common/hooks/useSelectableItemQueries";

export interface CustomCountryFieldProps {
    /** Lets a caller that owns the vertical rhythm itself turn the field's own margin off. */
    noMargin?: boolean;
    /** Puts the label above the input with the help icon beside it, for a form laid out that way. */
    useFixedLabel?: boolean;
    isLoading: boolean;
    entityId: string;
    entityApiPath: string;
    customInputFieldId: string;
    fieldName: string;
    fieldDescription: string | undefined;
    value: string;
    onChange: (changeFunc: (entity: any) => void) => void;
    readOnly?: boolean;
    isDisabled: boolean;
    errorText?: string;
    isPreview: boolean;
}

function CustomCountryField(props: CustomCountryFieldProps) {
    const { translateString, translateCountry } = useTranslation();
    const { onFieldChange } = useCustomField(props.entityApiPath, props.entityId, props.customInputFieldId, props.onChange);

    // The template preview only draws the field, so it asks for nothing: the same guard the other custom
    // select fields put on their option queries.
    const { data: countries, isLoading: countriesIsLoading } = useCountriesQuery(!props.isPreview);
    const selectableCountries = useMemo(
        () => countries?.map((country) => ({ name: translateCountry(country.countryCode), id: country.countryCode })) ?? [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [countries]
    );

    return (
        <DotLegalSelect
            noMargin={props.noMargin}
            useFixedLabel={props.useFixedLabel}
            isLoading={props.isLoading || countriesIsLoading}
            options={selectableCountries}
            onChange={props.readOnly ? () => {} : (value) => onFieldChange(value)}
            label={props.fieldName!}
            toolTipText={props.fieldDescription}
            noOptionsLabel={translateString("noOptions")}
            selectedItem={props.value}
            disabled={props.isDisabled}
            errorText={!isNullOrWhitespace(props.errorText) && !props.isPreview ? props.errorText : undefined}
            helperText={
                props.isPreview ? translateString(setFirstLetterToLowerCase(InputFieldTypeEnum[InputFieldTypeEnum.CountryField].toString())) : undefined
            }
        />
    );
}

export default CustomCountryField;
